import {alpha, Box, useTheme} from "@mui/material";
import {keyframes} from "@mui/material/styles";
import {AutomationStatus} from "crqlar-react-common/dist/models/automation/automation";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {ElementChip} from "./base/ElementChip";
import {getAutomationStatusColor} from "./colors";

export const AUTOMATION_STATUS_LABEL_KEYS: Record<AutomationStatus, string> = {
    [AutomationStatus.DRAFT]: 'Not live yet',
    [AutomationStatus.ACTIVE]: 'Live',
    [AutomationStatus.PAUSED]: 'Paused',
    [AutomationStatus.ARCHIVED]: 'Archived',
};

type AutomationStatusElementProps = {
    status: AutomationStatus | undefined;
    asPrint?: boolean;
    /**
     * The editor's live indicator: an ACTIVE flow becomes a green pill whose dot breathes.
     * **Only the automation editor passes this.** A table of automations would otherwise have a
     * row of pulsing dots going at once, which is a distraction, not a state — the list keeps the
     * neutral chip and a static dot. Every other status is unchanged either way.
     */
    live?: boolean;
};

export function AutomationStatusElement({
    status,
    asPrint = false,
    live = false
}: AutomationStatusElementProps) {
    const {t} = useTranslation();
    const theme = useTheme();

    const isLive = live && status === AutomationStatus.ACTIVE;

    /**
     * One soft ring leaving the dot every two seconds, ending at zero opacity — a heartbeat, not a
     * blink: nothing changes colour, nothing moves, and the dot itself never resizes. `box-shadow`
     * carries it so no extra element is needed, and `prefers-reduced-motion` drops it to a plain dot.
     */
    const pulse = useMemo(() => {
        const ring = theme.palette.success.main;
        return keyframes`
            0% { box-shadow: 0 0 0 0 ${alpha(ring, 0.35)}; }
            70% { box-shadow: 0 0 0 6px ${alpha(ring, 0)}; }
            100% { box-shadow: 0 0 0 0 ${alpha(ring, 0)}; }
        `;
    }, [theme]);

    if (!status) {
        return null;
    }

    const text = t(AUTOMATION_STATUS_LABEL_KEYS[status] ?? status);

    if (asPrint) {
        return <>{text}</>;
    }

    return (
        <Box sx={{display: "inline-flex", maxWidth: "100%"}}>
            <ElementChip
                text={text}
                borderColor={isLive ? alpha(theme.palette.success.main, 0.34) : undefined}
                backgroundColor={isLive ? alpha(theme.palette.success.main, 0.06) : undefined}
                startIcon={
                    <Box
                        component="span"
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: getAutomationStatusColor(status),
                            flex: "0 0 auto",
                            animation: isLive ? `${pulse} 2s ease-in-out infinite` : undefined,
                            "@media (prefers-reduced-motion: reduce)": {
                                animation: "none",
                            },
                        }}
                    />
                }
            />
        </Box>
    );
}
